import { isAxiosError } from 'axios'
import {
  isCancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
} from 'react-query'

import { handleException } from 'clients/exception-handling'
import { logger, type LoggerClient } from 'clients/logger'

export interface QueryErrorMeta {
  operation: string
  expectedErrorStatuses?: readonly number[]
  isExpectedError?: (error: unknown) => boolean
}

type RequestKind = 'query' | 'mutation'
type RequestMeta = Record<string, unknown> | undefined
type HttpErrorContext = {
  http_status: number | undefined
  http_method?: string
  request_id?: string
}

const requestContext = (error: unknown): HttpErrorContext => {
  if (!isAxiosError(error)) return { http_status: undefined }

  const method = error.config?.method?.toUpperCase()
  const requestId = error.response?.headers?.['x-request-id']
  const context: HttpErrorContext = {
    http_status: error.response?.status,
  }

  if (method) context.http_method = method
  if (
    typeof requestId === 'string' &&
    /^[a-zA-Z0-9_-]{1,128}$/.test(requestId)
  ) {
    context.request_id = requestId
  }
  return context
}

const isExpectedFailure = (
  error: unknown,
  meta: RequestMeta,
  status: number | undefined,
): boolean => {
  const predicate = meta?.['isExpectedError']
  if (typeof predicate === 'function' && predicate(error) === true) return true

  const statuses = meta?.['expectedErrorStatuses']
  return (
    status !== undefined && Array.isArray(statuses) && statuses.includes(status)
  )
}

/** Report after retries, once per failed query rather than per observer. */
export const createQueryClient = (
  reporter: LoggerClient = logger,
): QueryClient => {
  const report = (
    error: unknown,
    kind: RequestKind,
    meta?: Record<string, unknown>,
  ): void => {
    if (isCancelledError(error)) {
      handleException(
        error,
        {
          operation: `data.${kind}`,
          disposition: 'delegated',
          owner: 'react-query.cancellation',
        },
        reporter,
      )
      return
    }

    const context = requestContext(error)
    const operation = meta?.['operation']
    handleException(
      error,
      {
        operation: typeof operation === 'string' ? operation : `data.${kind}`,
        message: `Background ${kind} failed.`,
        disposition: 'expected',
        reason: 'The feature owns these expected request outcomes.',
        isExpectedError: (cause) =>
          isExpectedFailure(cause, meta, context.http_status),
        context: { kind, ...context },
      },
      reporter,
    )
  }

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => report(error, 'query', query.meta),
    }),
    mutationCache: new MutationCache({
      onError: (error, ...args) => {
        // React Query passes variables, context, then the mutation.
        const mutation = args[2]
        report(error, 'mutation', mutation.meta)
      },
    }),
  })
}
